import { useLayoutEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { useAuth } from '@/context/AuthContext';
import { getActiveSessionRevision, getAuthTokenForRequest, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { isDesktopOffline } from '@/api/desktopSync';
import { API } from '@/config/constants';
import { resolveBackendUrl } from '@/config/desktopBootstrap';
import { getDevices, renameDevice, retireDevice, assignPrimary, killPrimary } from '@/api/devices';
import { getPublicShopBranding } from '@/api/shop';

// TCA-26101816: non-secret command claims survive panel retirement, not reload.
// They serialize this renderer's tenant resource, not other windows or servers.
const pending = new Set();
const observers = new Set();
let sequence = 0;
const overlaps = (a, b) => a.endpoint === b.endpoint && (!a.tenant || !b.tenant || a.tenant === b.tenant);
const connection = () => JSON.stringify([window.lvbDesktop?.backendUrl, window.lvbDesktop?.localBackendUrl]);
const transportMatches = () => {
  const backend = resolveBackendUrl(window.location.protocol, window.lvbDesktop, process.env.REACT_APP_BACKEND_URL);
  return API !== null && backend !== null && `${backend || ''}/api` === API;
};
const principal = binding => JSON.stringify([binding?.user?.id, binding?.user?.tenant_id, binding?.source]);
const admin = binding => (binding?.user?.roles || [binding?.user?.role]).includes('admin');
const publicDevice = value => {
  if (!value || typeof value !== 'object' || Array.isArray(value)
    || typeof value.install_id !== 'string' || !value.install_id
    || typeof value.online !== 'boolean' || typeof value.is_primary !== 'boolean'
    || !['active', 'pending', 'legacy'].includes(value.authorization_status)) return null;
  const result = { install_id: value.install_id, online: value.online, is_primary: value.is_primary, authorization_status: value.authorization_status };
  for (const key of ['display_name', 'hostname', 'app_version', 'first_registered_at', 'last_seen_at']) {
    if (value[key] != null && typeof value[key] !== 'string') return null;
    result[key] = value[key] ?? null;
  }
  if (value.authorization_status !== 'active' && (value.online || value.is_primary)) return null;
  return result;
};
const outcome = (error, kind) => {
  const status = error?.response?.status; const detail = error?.response?.data?.detail;
  const refused = (status === 404 && detail === 'Device not found')
    || (kind === 'rename' && status === 422 && detail === 'display_name is required')
    || (kind === 'assign' && status === 409 && ['Device must complete secure registration before becoming primary',
      'Device is not currently online; it must be reachable to become primary'].includes(detail));
  if (refused) return { kind: 'rejected', message: detail };
  if ([401, 403].includes(status)) return { kind: 'rejected', message: 'This session cannot change device registration or authority. Review access before trying again.' };
  return { kind: 'unknown', message: 'The device change was not confirmed and may have taken effect. Refresh and review before a deliberate new action. Nothing will be resent automatically; an absent row does not prove retirement or that no primary exists.' };
};

export function createDeviceManagementController(authorize, resource) {
  const key = ++sequence; const listeners = new Set(); const reads = new Set();
  let active = false; let life = 0; let version = 0; let brandingVersion = 0;
  let snapshot = { devices: [], state: 'loading', known: false, timezone: null, active: false, writable: false, pending: false };
  const owns = (write = false) => active && !!authorize(write);
  const blocked = () => !owns(true) || [...pending].some(attempt => overlaps(resource, attempt));
  const notify = () => {
    snapshot = { ...snapshot, active: owns(), writable: owns(true), pending: [...pending].some(attempt => overlaps(resource, attempt)) };
    listeners.forEach(listener => listener());
  };
  const patch = update => { snapshot = { ...snapshot, ...update }; notify(); };
  const refresh = async () => {
    if (!owns()) return false;
    const started = life; const readVersion = ++version; const request = new AbortController();
    const current = () => owns() && started === life && readVersion === version && !request.signal.aborted;
    reads.add(request); patch({ state: 'loading' });
    try {
      const value = await getDevices({ ...authorize(), signal: request.signal });
      if (!current()) return false;
      if (!Array.isArray(value)) throw new Error('Invalid device list');
      const devices = value.map(publicDevice);
      if (devices.some(device => !device) || new Set(devices.map(device => device.install_id)).size !== devices.length
        || devices.filter(device => device.is_primary).length > 1) throw new Error('Invalid device list');
      patch({ devices, state: 'available', known: true }); return true;
    } catch { if (current()) patch({ state: 'unavailable' }); return false; }
    finally { reads.delete(request); }
  };
  const loadTimezone = async () => {
    if (!owns()) return;
    const started = life; const readVersion = ++brandingVersion; const request = new AbortController();
    const current = () => owns() && started === life && readVersion === brandingVersion && !request.signal.aborted;
    reads.add(request);
    try {
      const value = await getPublicShopBranding({ ...authorize(), signal: request.signal });
      if (!current()) return;
      const timezone = value?.timezone;
      if (typeof timezone !== 'string' || !timezone) return;
      new Intl.DateTimeFormat('en', { timeZone: timezone }).format();
      patch({ timezone });
    } catch { /* Optional presentation fact; default timezone remains available. */ }
    finally { reads.delete(request); }
  };
  const changed = (attempt, settled) => {
    if (!overlaps(resource, attempt)) return;
    ++version;
    if (snapshot.state === 'loading') snapshot = { ...snapshot, state: 'unavailable' };
    notify(); if (settled && owns()) void refresh();
  };
  const publish = (attempt, settled = false) => observers.forEach(observer => observer(attempt, settled));
  const mutate = async (kind, id, name) => {
    if (blocked()) return { kind: 'blocked' };
    const started = life; const config = authorize(true);
    const current = () => owns(true) && started === life;
    const attempt = { ...resource }; pending.add(attempt); publish(attempt);
    let result;
    try {
      if (!current()) return { kind: 'retired' };
      let value;
      if (kind === 'rename') value = await renameDevice(id, name, config);
      else if (kind === 'retire') value = await retireDevice(id, config);
      else if (kind === 'assign') value = await assignPrimary(id, config);
      else if (kind === 'revoke') value = await killPrimary(config);
      else throw new Error('Unknown device command');
      if (!current()) return { kind: 'retired' };
      if (value?.ok !== true || (kind === 'assign' && value.primary_install_id !== id)
        || (kind === 'revoke' && value.primary_install_id !== null)) throw new Error('Unconfirmed device change');
      result = { kind: 'accepted' };
    } catch (error) { result = outcome(error, kind); }
    finally { pending.delete(attempt); publish(attempt, true); }
    return current() ? result : { kind: 'retired' };
  };
  return {
    key, owns, blocked, refresh, mutate,
    getSnapshot: () => snapshot,
    subscribe: listener => { listeners.add(listener); return () => listeners.delete(listener); },
    activate() { active = true; ++life; observers.add(changed); notify(); if (owns()) { void refresh(); void loadTimezone(); } },
    retire() { active = false; ++life; ++version; ++brandingVersion; reads.forEach(request => request.abort()); observers.delete(changed); notify(); },
  };
}

export function useDeviceManagement() {
  const { sessionBinding } = useAuth() || {};
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  const identity = principal(sessionBinding); const source = connection(); const permitted = admin(sessionBinding);
  const latest = useRef(null); latest.current = { sessionBinding, source };
  const controller = useMemo(() => {
    const authorize = (write = false) => {
      const binding = latest.current.sessionBinding;
      if (!permitted || !binding?.user?.id || principal(binding) !== identity || binding.revision !== revision
        || revision !== getActiveSessionRevision() || !admin(binding)
        || source !== latest.current.source || source !== connection() || !transportMatches()
        || (write && (binding.source !== 'cloud' || isDesktopOffline()))) return null;
      const token = getAuthTokenForRequest(`${API}/devices`);
      return token ? { headers: { Authorization: `Bearer ${token}` }, timeout: 30000, suppressServerErrorNotification: true } : null;
    };
    return createDeviceManagementController(authorize, { endpoint: API, tenant: sessionBinding?.user?.tenant_id || null });
  }, [identity, revision, source, permitted, sessionBinding?.user?.tenant_id]);
  useLayoutEffect(() => { controller.activate(); return () => controller.retire(); }, [controller]);
  const snapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot);
  return { ...snapshot, controller, connectionError: !transportMatches(), local: isDesktopOffline() || sessionBinding?.source === 'offline' };
}
